// Schedule import (documents -> schedule / issues / checklists / minutes)
// and contractor-label mapping. Backend: back/routes/scheduleImport.js,
// mounted at /projects/:projectId/schedule-import.
import { url, tokenRef } from "api";

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem(tokenRef)}` });

const handle = async (res) => {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const e = new Error(data.err || `Request failed (${res.status})`);
    e.status = res.status; e.data = data;
    throw e;
  }
  return data;
};

const json = (method, path, body) =>
  fetch(`${url}${path}`, { method, headers: { "Content-Type": "application/json", ...auth() }, body: body === undefined ? undefined : JSON.stringify(body) }).then(handle);

/** Upload one document -> { importId, document, targets } (nothing written yet). */
export const parseImportDocument = (projectId, file, onProgress) =>
  new Promise((resolve, reject) => {
    const fd = new FormData();
    fd.append("file", file, file.name);
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${url}/projects/${projectId}/schedule-import/parse`);
    xhr.setRequestHeader("Authorization", auth().Authorization);
    if (xhr.upload && onProgress) xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100)); };
    xhr.onload = () => {
      let body = {};
      try { body = JSON.parse(xhr.responseText || "{}"); } catch (e) { /* ignore */ }
      if (xhr.status >= 200 && xhr.status < 300) resolve(body);
      else { const e = new Error(body.err || `Upload failed (${xhr.status})`); e.status = xhr.status; reject(e); }
    };
    xhr.onerror = () => reject(new Error("Upload failed"));
    xhr.send(fd);
  });

/** Pasted minutes text -> same shape as parseImportDocument. */
export const parseImportText = (projectId, text, name) => json("POST", `/projects/${projectId}/schedule-import/parse-text`, { text, name });

export const listImports = (projectId) => json("GET", `/projects/${projectId}/schedule-import`);
export const getImport = (projectId, importId) => json("GET", `/projects/${projectId}/schedule-import/${importId}`);
export const deleteImport = (projectId, importId) => json("DELETE", `/projects/${projectId}/schedule-import/${importId}`);
export const getImportTargets = (projectId) => json("GET", `/projects/${projectId}/schedule-import/targets`);
export const commitImport = (projectId, importId, mapping, contractorMap, options = {}) =>
  json("POST", `/projects/${projectId}/schedule-import/${importId}/commit`, { mapping, contractorMap, ...options });
/** Rows created by 'review first' imports still waiting: { imports: [{importId, fileName, importedAt, items: [{kind, id, title, sub, table, row}]}], total } */
export const listPendingImportItems = (projectId) => json("GET", `/projects/${projectId}/schedule-import/pending-items`);
export const decidePendingImportItems = (projectId, action, items) => json("POST", `/projects/${projectId}/schedule-import/pending-items/decide`, { action, items });

export const listImportTemplates = (projectId) => json("GET", `/projects/${projectId}/schedule-import/templates`);
export const deleteImportTemplate = (projectId, tid) => json("DELETE", `/projects/${projectId}/schedule-import/templates/${tid}`);

/** { labels: [{label, items, issues, unresolved*, companyId, userId, contacts}], choices: [{companyId, companyName, isOwner, users, roster}] } */
export const getContractorLabels = (projectId) => json("GET", `/projects/${projectId}/schedule-import/contractor-labels`);
export const saveContractorLabels = (projectId, labels, force = false) =>
  json("PUT", `/projects/${projectId}/schedule-import/contractor-labels`, { labels, force });
export const createPartnerFromLabel = (projectId, label, body = {}) =>
  json("POST", `/projects/${projectId}/schedule-import/contractor-labels/${encodeURIComponent(label)}/partner`, body);
